import { Card, Group, SimpleGrid, Stack, Text, useMantineColorScheme } from "@mantine/core";

export interface TeamStatsSectionProps {
  /** Aggregate team stats. Extend as API evolves. */
  stats: {
    matchesPlayed: number;
    wins: number;
    losses: number;
    draws?: number;
    roundDiff?: number; // rounds won - rounds lost
  };
  /** Optional section title (defaults to "Team Stats"). */
  title?: string;
  /** If true, the Card stretches to fill parent height; content scrolls on overflow. */
  fullHeight?: boolean;
}

/**
 * TeamStatsSection
 *
 * Displays high-level team statistics in a simple, responsive grid.
 * The colors are scheme-aware and rely on the Mantine theme. To adjust overall
 * surface and text contrast, change tokens in src/theme.ts (appThemes).
 */
export default function TeamStatsSection({ stats, title = 'Team Stats', fullHeight }: TeamStatsSectionProps) {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const items: Array<{ label: string; value: string | number }> = [
    { label: 'Matches Played', value: stats.matchesPlayed },
    { label: 'Wins', value: stats.wins },
    { label: 'Losses', value: stats.losses },
  ];
  if (typeof stats.draws === 'number') items.push({ label: 'Draws', value: stats.draws });
  if (typeof stats.roundDiff === 'number') items.push({ label: 'Round +/-', value: stats.roundDiff });

  return (
    <Card withBorder radius="md" p="md" style={{ height: fullHeight ? '100%' : undefined, width: '100%', display: 'flex' }}>
      <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
        <Text fw={700} size="lg" c={isDark ? 'gray.0' : 'dark.7'}>
          {title}
        </Text>
        {/* Grow the grid area and allow it to scroll if overflow occurs */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} spacing="sm">
            {items.map((it) => (
              <StatTile key={it.label} label={it.label} value={it.value} />
            ))}
          </SimpleGrid>
        </div>
      </Stack>
    </Card>
  );
}

function StatTile({ label, value }: { label: string; value: string | number }) {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  return (
    <Card withBorder radius="md" p="sm">
      <Stack gap={2}>
        {/* Use dimmed for labels in light, default in dark to keep good contrast */}
        <Text size="xs" c={isDark ? undefined : 'dimmed'} tt="uppercase" fw={700}>
          {label}
        </Text>
        <Text fw={800} size="lg">
          {value}
        </Text>
      </Stack>
    </Card>
  );
}
